import { Css, maybeTooltip, Palette, Tooltip, useTestIds } from "@homebound/beam";
import { formatToPrice, Percentage } from "src/components";
import { PRODUCT_EMPTY_STATE_IMG_URL } from "src/routes/libraries/product-catalog/components/product-images-viewer/ProductImageViewer";
import {
  AddProductOfferingConfig_ReadyPlanOptionsFragment,
  RpoDeltaFragment,
} from "src/routes/libraries/product-offering-configs/AddProductOfferingConfigPage.generated";
import { isDefined } from "src/utils";
import { ConfigOptionsAndDelta } from "../AddProductOfferingConfigPage";
import { ConflictTag } from "./ConflictTag";
import { OptionInCheckoutTag } from "./OptionInCheckoutTag";
import { GroupedOptionsProps } from "./OptionsByOptionGroupSection";
import { OptionSelect } from "./OptionSelect";
import { PrereqTag } from "./PrereqTag";

export function OptionSection(
  props: Omit<GroupedOptionsProps, "groupedRpos"> & {
    configData: ConfigOptionsAndDelta;
    disabled: boolean;
    conflicts: string[];
    missingPreReqs: string[];
    disallowedInBaseConfig: boolean;
  },
) {
  const { formState, recomputeCosts, configData, disabled, missingPreReqs, conflicts, disallowedInBaseConfig } = props;
  const { rpo, delta } = configData;
  const isSelected = formState.value.readyPlanOptions?.some((os) => os.readyPlanOptionId === rpo.id) ?? false;

  const tid = useTestIds({});

  // For design packages, combine the type and option group name such as `Design Package - Interior Style` since the groups no longer have subheadings
  const typeDisplayname =
    (rpo.type.forDesignInterior || rpo.type.forDesignExterior) && rpo.type.name !== rpo.optionGroup.name
      ? `${rpo.type.name} - ${rpo.optionGroup.name}`
      : rpo.type.name;

  const disabledTooltip = disabled ? (
    <>
      {missingPreReqs.nonEmpty && (
        <p>This option is disabled because it has missing pre-requisite(s): {missingPreReqs.join(", ")}.</p>
      )}
      {conflicts.nonEmpty && (
        <p>This option is disabled because it has conflict(s) with other selected options: {conflicts.join(", ")}.</p>
      )}
      {disallowedInBaseConfig && (
        <p>This option is unavailable in base configurations as it's not available within Checkout.</p>
      )}
      {!rpo.active && <p>This option is not active in the current offering.</p>}
    </>
  ) : null;

  return maybeTooltip({
    title: disabledTooltip,
    children: (
      <div
        key={rpo.id}
        css={Css.dg.gtc("96px 1fr 24px").gtr("20px 1fr").cg2.mb4.add("transition", "opacity 0.3s ease").$}
      >
        <OptionImageOrPlaceholder rpo={rpo} disabled={disabled} />
        <h3 css={Css.gr(1).gc(2).xsSb.gray700.if(disabled).o50.$}>{typeDisplayname}</h3>
        <div css={Css.gr(2).gc(2).if(disabled).o50.$}>
          <div css={Css.df.gap1.aic.jcsb.smSb.gray900.$} {...tid.opt}>
            <h4 css={Css.$}>{[rpo.name, rpo.location?.name].compact().join(" - ")}</h4>
            <div css={Css.df.gap2.aic.jcsb.$}>{displayAmount("Cost", delta?.totalCostInCents)}</div>
          </div>
          <OptionDeltaDetails delta={delta} />
          <div css={Css.df.gap1.aic.jcfs.pt1.$}>
            {conflicts.nonEmpty && <ConflictTag conflicts={conflicts} asTag={false} />}
            {rpo.optionPrerequisites.nonEmpty && <PrereqTag preReqs={rpo.optionPrerequisites} />}
            <OptionInCheckoutTag showInCheckout={rpo.showInCheckout} />
          </div>
        </div>
        <div css={Css.gc(3).gr(2).$}>
          <OptionSelect
            rpo={rpo}
            // Allow deselecting any currently selected option, even if it would otherwise be disabled
            disabled={!isSelected && (conflicts.nonEmpty || missingPreReqs.nonEmpty || disallowedInBaseConfig)}
            formState={formState}
            recomputeCosts={recomputeCosts}
          />
        </div>
      </div>
    ),
  });
}

export function OptionImageOrPlaceholder({
  rpo,
  disabled,
  onCostSummary = false,
}: {
  rpo: AddProductOfferingConfig_ReadyPlanOptionsFragment;
  disabled: boolean;
  onCostSummary?: boolean;
}) {
  const tid = useTestIds({}, "optionImage");
  const imageSizePx = onCostSummary ? 40 : 96;

  // For design packages, display the initials of the option name as we don't expect to have an option image
  if (rpo.type.forDesignInterior) {
    const color = rpo.globalOption.group.designPackageTagColor ?? Palette.Purple500;

    return (
      <div
        css={
          Css.gc(1)
            .gr("1 / 3")
            .ba.br8.bcGray300.bgColor(color)
            .wPx(imageSizePx)
            .hPx(imageSizePx)
            .fs0.df.aic.jcc.if(disabled).o50.$
        }
        {...tid}
      >
        <span css={Css.xl.white.$}>{getOptionInitials(rpo.name)}</span>
      </div>
    );
  }

  return (
    <img
      css={
        Css.gc(1).gr("1 / 3").ba.br8.bcGray300.objectFit("cover").wPx(imageSizePx).hPx(imageSizePx).if(disabled).o50.$
      }
      src={rpo.globalOption.asset?.previewUrl ?? PRODUCT_EMPTY_STATE_IMG_URL}
      alt={rpo.name}
      {...tid}
    />
  );
}

function OptionDeltaDetails({ delta }: { delta?: RpoDeltaFragment }) {
  if (!delta) return null;

  return (
    <div css={Css.df.gap2.sm.gray700.$}>
      <span>{displayAmount("List Price", delta.preCutoffTotalPriceInCents)}</span>
      <span>
        Margin: <Percentage percent={delta.preCutoffDesiredMarginInBasisPoints} basisPoints />
      </span>
      <Tooltip
        title={
          <>
            {delta.postCutoffDesiredMarginInBasisPoints && (
              <p>
                Post-Cutoff Margin <Percentage percent={delta.postCutoffDesiredMarginInBasisPoints} basisPoints />
              </p>
            )}
            {delta.postCutoffTotalPriceInCents && (
              <p>Post-Cutoff Price {formatToPrice({ valueInCents: delta.postCutoffTotalPriceInCents })}</p>
            )}
            <p>Selection Fee {formatToPrice({ valueInCents: delta.selectionFeeInCents })}</p>
            <p>Minimum Price {formatToPrice({ valueInCents: delta.minimumPriceInCents })}</p>
          </>
        }
      >
        <span css={Css.cursorPointer.tdu.$}>Details</span>
      </Tooltip>
    </div>
  );
}

/** Display the first two letters of each word in the option name
 * Ex: "Mountain Contemporary" -> "Mo Co" */
export function getOptionInitials(name: string) {
  return name
    .split(" ")
    .map((word) => {
      const firstTwo = word.slice(0, 2);
      return firstTwo.charAt(0).toUpperCase() + firstTwo.slice(1).toLowerCase();
    })
    .join(" ");
}

export function displayAmount(prefix: string, amountInCents: number | undefined) {
  const amount = isDefined(amountInCents) && amountInCents !== 0 ? formatToPrice({ valueInCents: amountInCents }) : "—";

  return `${prefix} ${amount}`;
}
